import React, { useState, useEffect } from 'react';
import { CalendarIcon, ClockIcon } from '@heroicons/react/24/outline';
import { calculateAvailability, getAvailableDates, parseYmdLocal } from '../../lib/availability';
import { TimeSlot } from '../../stores/useSchedulingStore';
import { logger } from '../../utils/logger';

interface CalendarPickerProps {
  empresaId: string;
  selectedDate: string | null;
  selectedTime: string | null;
  onSelect: (date: string, time: string) => void;
  minDate?: Date;
  maxDate?: Date;
  /** Texto del recuadro de confirmación (p. ej. entrega vs cita). */
  selectedEventTitle?: string;
  /** Conector antes de la hora en el resumen (p. ej. «a las»). */
  selectedEventTimePrefix?: string;
}

export const CalendarPicker: React.FC<CalendarPickerProps> = ({
  empresaId,
  selectedDate,
  selectedTime,
  onSelect,
  minDate,
  maxDate: _maxDate,
  selectedEventTitle = 'Cita seleccionada:',
  selectedEventTimePrefix = 'a las',
}) => {
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [loadingDates, setLoadingDates] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [error, setError] = useState<string | null>(null);

  // Load available dates
  useEffect(() => {
    const loadAvailableDates = async () => {
      setLoadingDates(true);
      setError(null);
      try {
        const dates = await getAvailableDates(empresaId, minDate || new Date(), 60);
        setAvailableDates(dates);
      } catch (err: any) {
        logger.error('Error loading available dates:', err);
        setError('Error al cargar fechas disponibles');
      } finally {
        setLoadingDates(false);
      }
    };

    if (empresaId) {
      loadAvailableDates();
    }
  }, [empresaId, minDate]);

  // Load time slots when date is selected
  useEffect(() => {
    const loadTimeSlots = async () => {
      if (!selectedDate || !empresaId) {
        setTimeSlots([]);
        return;
      }

      setLoadingSlots(true);
      setError(null);
      try {
        const slots = await calculateAvailability(empresaId, selectedDate);
        setTimeSlots(slots);
      } catch (err: any) {
        logger.error('Error loading time slots:', err);
        setError('Error al cargar horarios disponibles');
        setTimeSlots([]);
      } finally {
        setLoadingSlots(false);
      }
    };

    loadTimeSlots();
  }, [selectedDate, empresaId]);

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: (Date | null)[] = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const formatDate = (date: Date): string => {
    // Use local date methods to avoid timezone conversion issues
    // toISOString() converts to UTC which can shift the date by one day
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const isDateAvailable = (date: Date) => {
    const dateStr = formatDate(date);
    return availableDates.includes(dateStr);
  };

  const isDateSelected = (date: Date) => {
    return selectedDate === formatDate(date);
  };

  const isDatePast = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const handleDateClick = (date: Date) => {
    if (!isDateAvailable(date) || isDatePast(date)) return;
    const dateStr = formatDate(date);
    onSelect(dateStr, '');
  };

  const handleTimeClick = (time: string) => {
    if (selectedDate) {
      onSelect(selectedDate, time);
    }
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth((prev) => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(prev.getMonth() - 1);
      } else {
        newDate.setMonth(prev.getMonth() + 1);
      }
      return newDate;
    });
  };

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const weekDays = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  const days = getDaysInMonth(currentMonth);

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
          {error}
        </div>
      )}

      {/* Calendar */}
      <div className="border rounded-lg p-4 bg-white">
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={() => navigateMonth('prev')}
            className="p-2 hover:bg-gray-100 rounded-md"
            aria-label="Mes anterior"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h3 className="text-lg font-semibold text-gray-900">
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </h3>
          <button
            type="button"
            onClick={() => navigateMonth('next')}
            className="p-2 hover:bg-gray-100 rounded-md"
            aria-label="Mes siguiente"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Week day headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {weekDays.map((day) => (
            <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar days */}
        {loadingDates ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            {availableDates.length === 0 && !error ? (
              <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900">
                No hay fechas disponibles en los próximos 60 días. Configura horarios en{' '}
                <strong>Configuración → Agenda</strong> (días laborables, cupos y bloqueos).
              </div>
            ) : null}
            <div className="grid grid-cols-7 gap-1">
            {days.map((day, index) => {
              if (!day) {
                return <div key={`empty-${index}`} className="aspect-square" />;
              }

              const dateStr = formatDate(day);
              const available = isDateAvailable(day);
              const selected = isDateSelected(day);
              const past = isDatePast(day);
              const isToday = formatDate(new Date()) === dateStr;

              return (
                <button
                  type="button"
                  key={dateStr}
                  onClick={() => handleDateClick(day)}
                  disabled={!available || past}
                  className={`
                    aspect-square rounded-md text-sm font-medium transition-colors
                    ${selected
                      ? 'bg-blue-600 text-white'
                      : available && !past
                      ? 'bg-white text-gray-900 hover:bg-blue-50 border border-gray-200'
                      : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                    }
                    ${isToday && !selected ? 'ring-2 ring-blue-500' : ''}
                  `}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>
          </>
        )}
      </div>

      {/* Time Slots */}
      {selectedDate && (
        <div className="border rounded-lg p-4 bg-white">
          <div className="flex items-center space-x-2 mb-4">
            <ClockIcon className="h-5 w-5 text-gray-500" />
            <h3 className="text-lg font-semibold text-gray-900">
              Horarios Disponibles -{' '}
              {parseYmdLocal(selectedDate).toLocaleDateString('es-CO', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </h3>
          </div>

          {loadingSlots ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : timeSlots.length === 0 ? (
            <p className="text-gray-500 text-center py-4">
              No hay horarios disponibles para esta fecha
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {timeSlots.map((slot) => (
                <button
                  type="button"
                  key={slot.time}
                  onClick={() => handleTimeClick(slot.time)}
                  disabled={!slot.available}
                  className={`
                    px-4 py-2 rounded-md text-sm font-medium transition-colors
                    ${selectedTime === slot.time
                      ? 'bg-blue-600 text-white'
                      : slot.available
                      ? 'bg-white text-gray-900 hover:bg-blue-50 border border-gray-200'
                      : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                    }
                  `}
                >
                  <div>{slot.time}</div>
                  {slot.available && (
                    <div className="text-xs text-gray-500 mt-1">
                      {slot.availableCount} espacio{slot.availableCount !== 1 ? 's' : ''} disponible
                      {slot.availableCount !== 1 ? 's' : ''}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {selectedDate && selectedTime && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <CalendarIcon className="h-5 w-5 text-blue-600" />
            <div>
              <div className="font-medium text-blue-900">{selectedEventTitle}</div>
              <div className="text-sm text-blue-700">
                {parseYmdLocal(selectedDate).toLocaleDateString('es-CO', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}{' '}
                {selectedEventTimePrefix} {selectedTime}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarPicker;
